import React from 'react';
import { Link } from 'react-router-dom';
import { useThemeContext } from '../contexts/ThemeContext';
import ParticleBackground from '../components/ParticleBackground';
import backIcon from '../assets/images/arrow_back.png';
import privacyIcon from '../assets/images/padlock.png';

const PrivacyPolicyPage: React.FC = () => {
  const { isDark } = useThemeContext();

  return (
    <div className="min-h-screen flex flex-col lg:flex-row relative">
      {/* Particle Background */}
      <ParticleBackground />
      
      {/* Privacy Policy Content */}
      <div className="w-full flex items-center justify-center p-4">
        <div className="max-w-4xl w-full bg-light p-6 md:p-8 rounded-lg shadow-md">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <img src={privacyIcon} alt="Privacy Policy" className="w-6 h-6 mr-3" />
              <h1 className="text-2xl font-medium text-dark">Privacy Policy</h1>
            </div>
            <Link
              to="/login"
              className="text-primary hover:text-primary-variant font-medium transition-colors duration-200 inline-flex items-center"
            >
              <img src={backIcon} alt="Back" className="w-4 h-4 mr-2" />
              Back
            </Link>
          </div>
          
          <p className="text-grey_dark mb-6">Last Updated: April 12, 2026</p>

          <div className="prose max-w-none">
            <p className="text-grey_dark mb-6">
              At Balancio, we are committed to protecting your business data and personal information. 
              This Privacy Policy outlines how we collect, use, and safeguard the information you provide 
              when using our platform.
            </p>

            <div className="space-y-8">
              {/* Section 1 */}
              <section>
                <h2 className="text-xl font-bold text-dark mb-4 flex items-center">
                  <span className="border border-grey_light text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3">1</span>
                  Information We Collect
                </h2>
                <p className="text-grey_dark mb-4">
                  We collect information to provide a better experience for our users. This includes:
                </p>
                <ul className="list-disc pl-6 text-grey_dark space-y-2">
                  <li>
                    <strong>Account Information:</strong> When you register, we collect your name, email address, and password.
                  </li>
                  <li>
                    <strong>Business & Financial Data:</strong> To provide our services, we process data you input, such as revenue, expenses, and profit margins.
                  </li>
                  <li>
                    <strong>Payment Information:</strong> Payment processing is handled by third-party providers (e.g., Flutterwave). We do not store your full credit card details on our servers.
                  </li>
                  <li>
                    <strong>Usage Data:</strong> We collect information on how you interact with Balancio, including IP addresses, browser types, and pages visited.
                  </li>
                </ul>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-xl font-bold text-dark mb-4 flex items-center">
                  <span className="border border-grey_light text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3">2</span>
                  How We Use Your Information
                </h2>
                <p className="text-grey_dark mb-4">
                  We use the collected data for the following purposes:
                </p>
                <ul className="list-disc pl-6 text-grey_dark space-y-2">
                  <li>To provide, maintain, and improve the Balancio platform.</li>
                  <li>To process transactions and send related information, such as confirmations and invoices.</li>
                  <li>To communicate with you about updates, security alerts, and support.</li>
                  <li>To monitor and analyze trends and usage to enhance user experience.</li>
                </ul>
              </section>

              {/* Section 3 */}
              <section>
                <h2 className="text-xl font-bold text-dark mb-4 flex items-center">
                  <span className="border border-grey_light text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3">3</span>
                  Data Protection & Security
                </h2>
                <p className="text-grey_dark mb-4">
                  We implement industry-standard security measures to protect your data:
                </p>
                <ul className="list-disc pl-6 text-grey_dark space-y-2">
                  <li>
                    <strong>Encryption:</strong> We use SSL/TLS encryption for data in transit and robust encryption methods for sensitive data at rest.
                  </li>
                  <li>
                    <strong>Access Control:</strong> Access to user data is strictly limited to authorized personnel who require it to perform their duties.
                  </li>
                </ul>
              </section>

              {/* Section 4 */}
              <section>
                <h2 className="text-xl font-bold text-dark mb-4 flex items-center">
                  <span className="border border-grey_light text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3">4</span>
                  Sharing of Information
                </h2>
                <p className="text-grey_dark mb-4">
                  We do not sell, trade, or rent your personal or business data to third parties. 
                  We may share information only in the following circumstances:
                </p>
                <ul className="list-disc pl-6 text-grey_dark space-y-2">
                  <li>
                    <strong>Service Providers:</strong> With third-party vendors who perform services on our behalf (e.g., hosting, payment processing, or analytics).
                  </li>
                  <li>
                    <strong>Legal Requirements:</strong> If required by law or in response to valid requests by public authorities.
                  </li>
                </ul>
              </section>

              {/* Section 5 */}
              <section>
                <h2 className="text-xl font-bold text-dark mb-4 flex items-center">
                  <span className="border border-grey_light text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3">5</span>
                  Your Rights & Choices
                </h2>
                <p className="text-grey_dark mb-4">
                  You have control over your data:
                </p>
                <ul className="list-disc pl-6 text-grey_dark space-y-2">
                  <li>
                    <strong>Access & Update:</strong> You can update your account information directly within the Balancio settings.
                  </li>
                  <li>
                    <strong>Data Deletion:</strong> You may request the deletion of your account and associated data at any time by contacting support.
                  </li>
                  <li>
                    <strong>Opt-Out:</strong> You can opt-out of promotional communications by following the unsubscribe instructions in our emails.
                  </li>
                </ul>
              </section>

              {/* Section 6 */}
              <section>
                <h2 className="text-xl font-bold text-dark mb-4 flex items-center">
                  <span className="border border-grey_light text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3">6</span>
                  Cookies
                </h2>
                <p className="text-grey_dark">
                  Balancio uses cookies to enhance your experience. You can choose to disable cookies through your browser settings, 
                  though some features of the platform may not function properly as a result.
                </p>
              </section>

              {/* Section 7 */}
              <section>
                <h2 className="text-xl font-bold text-dark mb-4 flex items-center">
                  <span className="border border-grey_light text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3">7</span>
                  Changes to This Policy
                </h2>
                <p className="text-grey_dark">
                  We may update this Privacy Policy from time to time. We will notify you of any significant changes 
                  by posting the new policy on our website and updating the "Last Updated" date.
                </p>
              </section>

              {/* Section 8 */}
              <section>
                <h2 className="text-xl font-bold text-dark mb-4 flex items-center">
                  <span className="border border-grey_light text-primary rounded-full w-8 h-8 flex items-center justify-center mr-3">8</span>
                  Contact Us
                </h2>
                <p className="text-grey_dark mb-4">
                  If you have any questions about this Privacy Policy, please contact us at:
                </p>
                <div className="bg-bg_grey_light p-4 rounded-lg">
                  <p className="text-dark font-medium">
                    Email: <a href="mailto:support@balancio.in" className="text-primary hover:text-primary-variant">support@balancio.in</a>
                  </p>
                </div>
              </section>
            </div>

            {/* Footer */}
            <div className="mt-12 pt-8 border-t border-grey_light">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-grey_dark text-sm">
                  © {new Date().getFullYear()} Balancio. All rights reserved.
                </p>
                <div className="flex gap-4">
                  <Link
                    to="/login"
                    className="text-primary hover:text-primary-variant font-medium text-sm"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="text-primary hover:text-primary-variant font-medium text-sm"
                  >
                    Sign Up
                  </Link>
                  <Link
                    to="/forgot-password"
                    className="text-primary hover:text-primary-variant font-medium text-sm"
                  >
                    Forgot Password
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;